import { type Metrics } from '@atlassian/metrics-interface';
import { type Logger } from '@atlassian/logger-interface';
import { HIGH_CARDINALITY_METRIC_TAG_OVERRIDES } from '@post-office/high-cardinality-metric-tag-overrides';

type MessagePerformanceMetricTags = {
	messageTemplateId: string;
	operation: string;
	stage: InstrumentedStage;
};

type RecipientType =
	| 'AccountId'
	| 'GroupedRecipient'
	| 'EmailAddress'
	| 'SlackChannel'
	| 'SlackUser';

type PullChannelMessagePerformanceMetricNameSegments = {
	flow: 'pull';
	placementId: string;
};

type PushChannelMessagePerformanceMetricNameSegments = {
	flow: 'push';
	recipientType: RecipientType;
};

type ExecutionLayer = 'message' | 'orchestration';

type OperationSequenceEntry = {
	messageTemplateId: string;
	stage: InstrumentedStage;
	timeMs: number;
	layer: ExecutionLayer;
};

type OperationTimeInterval = {
	startTimeMs: number;
	endTimeMs: number;
};

type MeasureOperationContext = {
	layer: ExecutionLayer;
};

type InstrumentedStage = 'data-retrieval' | 'filtering' | 'hydration';

const PULL_MESSAGE_PERFORMANCE_PER_PLACEMENT = 'message-performance.pull.per-placement';
const PUSH_MESSAGE_PERFORMANCE_PER_RECIPIENT_TYPE = 'message-performance.push.per-recipient-type';

const HISTOGRAM_BUCKETS = {
	pull: {
		perStage: [10, 25, 50, 100, 150, 200, 250, 300, 400, 500],
		endToEnd: [25, 100, 200, 300, 400, 500],
	},
	push: {
		perStage: [100, 500, 1000],
		endToEnd: [100, 500, 1000, 5000],
	},
};

type Options = {
	logThreshold?: number;
	trackEndToEnd?: boolean;
	metricNameSegments:
		| PullChannelMessagePerformanceMetricNameSegments
		| PushChannelMessagePerformanceMetricNameSegments;
};

const defaultOptions = {
	logThreshold: 500,
	trackEndToEnd: false,
};

type MessageTemplateId = string;

/**
 * Tracks performance for message processing.
 *
 * Responsibilities:
 * 1) Measure each operation and emit timer + histogram metrics.
 * 2) Keep a per message template id per layer cumulative timer -
 * 	how much time was spend running all stages of message maker's code for a given message VS how much time was spend running Post Office orchestration code.
 * 3) Optionally track end-to-end timing and emit logs if the total execution time exceeds a specified threshold.
 *
 * Public API is intentionally small:
 * - `measureOperation`: wraps one async operation.
 * - `stop`: flushes cumulative layer timings and final logging/metrics.
 */
export class MessagePerformance {
	private readonly startTime = performance.now();
	// Keeps only the longest duration per stage+template combination for threshold logs.
	private readonly slowestOperationPerStagePerMessage = new Map<
		`${InstrumentedStage}:${MessageTemplateId}`,
		OperationSequenceEntry
	>();
	// Tracks operation intervals per execution layer and message template id.
	private readonly operationIntervalsPerLayerPerMessage = new Map<
		`${ExecutionLayer}:${MessageTemplateId}`,
		OperationTimeInterval[]
	>();

	constructor(
		private readonly observability: { metrics: Metrics; logger: Logger },
		private readonly options: Options,
	) {}

	/**
	 * Wraps one async operation with timing instrumentation.
	 *
	 * Flow:
	 * 1) Start stage metrics (timer + histogram).
	 * 2) Execute callback.
	 * 3) Record layer + longest-stage bookkeeping.
	 * 4) Stop stage metrics with success/failure status.
	 */
	async measureOperation<T>(
		callback: () => Promise<T>,
		tags: MessagePerformanceMetricTags,
		context: MeasureOperationContext,
	): Promise<T> {
		const operationStart = performance.now();
		const stageMetricName = this.getMetricName(tags.stage);
		const histogramBuckets = HISTOGRAM_BUCKETS[this.options.metricNameSegments.flow].perStage;

		const histogram = this.observability.metrics
			.histogram(stageMetricName, histogramBuckets)
			.measure();
		const timer = this.observability.metrics.timing(stageMetricName).measure();

		let status: 'success' | 'failure' = 'success';

		try {
			return await callback();
		} catch (error) {
			status = 'failure';
			throw error;
		} finally {
			const operationEndTime = performance.now();
			const operationLatency = operationEndTime - operationStart;

			this.recordOperationIntervalPerLayerPerMessage(
				tags.messageTemplateId,
				context.layer,
				operationStart,
				operationEndTime,
			);
			this.updateSlowestOperationPerStagePerMessage(tags, context.layer, operationLatency);

			const operationMetricTags = this.applyTagOverrides({ ...tags, status });
			histogram.stop(operationMetricTags);
			timer.stop(operationMetricTags);
		}
	}

	/**
	 * Finalizes recording for this instance.
	 *
	 * Always publishes per-layer cumulative timings.
	 * If end-to-end is enabled, also stops e2e metrics and logs slow executions.
	 */
	stop(): void {
		this.publishTotalLayerLatencyPerMessage();

		if (!this.options.trackEndToEnd) {
			return;
		}

		const totalTime = performance.now() - this.startTime;

		this.publishEndToEndFlowLatency(totalTime);
		this.logThresholdExceeded(totalTime);
	}

	private logThresholdExceeded(totalTime: number) {
		const logThreshold = this.options.logThreshold ?? defaultOptions.logThreshold;
		const shouldLogThresholdExceeded =
			totalTime > logThreshold && this.slowestOperationPerStagePerMessage.size > 0;

		if (shouldLogThresholdExceeded) {
			const operationSequence = Array.from(this.slowestOperationPerStagePerMessage.values());

			this.observability.logger.info(
				{
					totalTime,
					logThreshold,
					operationSequence,
				},
				'Message performance threshold exceeded',
			);
		}
	}

	private getMetricName(suffix: InstrumentedStage | ExecutionLayer | 'e2e'): string {
		return this.options.metricNameSegments.flow === 'pull'
			? `${PULL_MESSAGE_PERFORMANCE_PER_PLACEMENT}.${this.options.metricNameSegments.placementId}.${suffix}`
			: `${PUSH_MESSAGE_PERFORMANCE_PER_RECIPIENT_TYPE}.${this.options.metricNameSegments.recipientType}.${suffix}`;
	}

	private applyTagOverrides<T extends Record<string, string>>(tags: T) {
		return {
			...tags,
			...HIGH_CARDINALITY_METRIC_TAG_OVERRIDES,
		};
	}

	private recordOperationIntervalPerLayerPerMessage(
		messageTemplateId: MessageTemplateId,
		layer: ExecutionLayer,
		operationStartTimeMs: number,
		operationEndTimeMs: number,
	): void {
		const key = `${layer}:${messageTemplateId}` as const;
		const existingIntervals = this.operationIntervalsPerLayerPerMessage.get(key) ?? [];
		existingIntervals.push({
			startTimeMs: operationStartTimeMs,
			endTimeMs: operationEndTimeMs,
		});
		this.operationIntervalsPerLayerPerMessage.set(key, existingIntervals);
	}

	private calculateTotalDurationForLayers(intervals: OperationTimeInterval[]): number {
		if (intervals.length === 0) {
			return 0;
		}

		const sortedIntervals = intervals.toSorted((firstInterval, secondInterval) => {
			return firstInterval.startTimeMs - secondInterval.startTimeMs;
		});

		const mergedIntervals: OperationTimeInterval[] = [];

		for (const currentInterval of sortedIntervals) {
			const lastMergedInterval = mergedIntervals[mergedIntervals.length - 1];

			// Start a new merged interval when there is no previous interval,
			// or when the current interval starts after the previous one ends.
			if (!lastMergedInterval || currentInterval.startTimeMs > lastMergedInterval.endTimeMs) {
				mergedIntervals.push({ ...currentInterval });
				continue;
			}

			lastMergedInterval.endTimeMs = Math.max(
				lastMergedInterval.endTimeMs,
				currentInterval.endTimeMs,
			);
		}

		return mergedIntervals.reduce(
			(totalDuration, interval) => totalDuration + (interval.endTimeMs - interval.startTimeMs),
			0,
		);
	}

	private updateSlowestOperationPerStagePerMessage(
		tags: MessagePerformanceMetricTags,
		layer: ExecutionLayer,
		durationMs: number,
	): void {
		const key = `${tags.stage}:${tags.messageTemplateId}` as const;
		const existing = this.slowestOperationPerStagePerMessage.get(key);

		if (!existing || durationMs > existing.timeMs) {
			this.slowestOperationPerStagePerMessage.set(key, {
				messageTemplateId: tags.messageTemplateId,
				stage: tags.stage,
				timeMs: durationMs,
				layer,
			});
		}
	}

	private publishTotalLayerLatencyPerMessage(): void {
		for (const [layer, operationIntervals] of this.operationIntervalsPerLayerPerMessage) {
			const [layerName, messageTemplateId] = layer.split(':');
			const metricName = this.getMetricName(layerName as ExecutionLayer);
			const totalLayerDuration = this.calculateTotalDurationForLayers(operationIntervals);

			const buckets = HISTOGRAM_BUCKETS[this.options.metricNameSegments.flow].endToEnd;
			const tags = this.applyTagOverrides({ messageTemplateId: messageTemplateId! });
			this.observability.metrics.timing(metricName, tags).set(totalLayerDuration);
			this.observability.metrics.histogram(metricName, buckets, tags).set(totalLayerDuration);
		}
	}

	private publishEndToEndFlowLatency(totalTime: number): void {
		const metricName = this.getMetricName('e2e');
		const buckets = HISTOGRAM_BUCKETS[this.options.metricNameSegments.flow].endToEnd;
		const tags = this.applyTagOverrides({});
		this.observability.metrics.timing(metricName, tags).set(totalTime);
		this.observability.metrics.histogram(metricName, buckets, tags).set(totalTime);
	}
}
